/**
 * Calendar Filter Manager
 *
 * Business logic for grouping and filtering calendars and family labels.
 * Follows single responsibility principle - handles filter section building only.
 */

import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import type { FamilyMember } from '@/types/family-member';
import type { FilterSection, FilterItem } from '@/types/calendar-filter';

/**
 * Manages calendar filter logic including grouping by source
 * and building unified filter sections.
 */
export class CalendarFilterManager {
  /**
   * Groups calendars by their source/account name
   */
  static groupCalendarsBySource(calendars: DeviceCalendar[]): Map<string, DeviceCalendar[]> {
    const groups = new Map<string, DeviceCalendar[]>();

    calendars.forEach(calendar => {
      const sourceKey = calendar.source.name;
      const existing = groups.get(sourceKey) || [];
      existing.push(calendar);
      groups.set(sourceKey, existing);
    });

    return groups;
  }

  /**
   * Gets a human-readable display name for a calendar source
   */
  static getSourceDisplayName(source: { name: string; type: string }): string {
    // Common source type mappings
    const typeNames: Record<string, string> = {
      'com.google': 'Google Calendar',
      'com.apple.icloud': 'iCloud',
      'local': 'On this device',
      'subscribed': 'Subscribed',
      'birthdays': 'Birthdays',
      'exchange': 'Exchange',
    };

    // Check if source name already has a meaningful name
    if (source.name && source.name !== source.type) {
      return source.name;
    }

    return typeNames[source.type] || source.name || 'Calendar';
  }

  /**
   * Builds unified filter sections from calendars and family members
   */
  static buildFilterSections(
    calendars: DeviceCalendar[],
    familyMembers: FamilyMember[],
    selectedCalendarIds: string[],
    selectedLabelIds: string[]
  ): FilterSection[] {
    const sections: FilterSection[] = [];

    // Group calendars by source
    const calendarGroups = this.groupCalendarsBySource(calendars);

    // Sort groups: user's primary calendar source first, then alphabetically
    const primaryCalendar = calendars.find(c => c.isPrimary);
    const primarySourceName = primaryCalendar?.source.name;

    const sortedGroupKeys = Array.from(calendarGroups.keys()).sort((a, b) => {
      if (a === primarySourceName) return -1;
      if (b === primarySourceName) return 1;
      return a.localeCompare(b);
    });

    // Build calendar sections
    sortedGroupKeys.forEach(sourceKey => {
      const sourceCalendars = calendarGroups.get(sourceKey) || [];
      if (sourceCalendars.length === 0) return;

      const items: FilterItem[] = sourceCalendars.map(calendar => ({
        id: calendar.id,
        type: 'calendar' as const,
        name: calendar.title,
        color: calendar.color,
        isSelected: selectedCalendarIds.includes(calendar.id),
        subtitle: !calendar.allowsModifications ? 'Read-only' : undefined,
        isPrimary: calendar.isPrimary,
        isHiddenInNativeApp: calendar.isVisible === false,
      }));

      // Sort items: primary first, then alphabetically
      items.sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return a.name.localeCompare(b.name);
      });

      const displayName = this.getSourceDisplayName(sourceCalendars[0].source);

      sections.push({
        id: `calendar-${sourceKey}`,
        title: displayName,
        items,
      });
    });

    // Add family labels section if there are family members
    if (familyMembers.length > 0) {
      const labelItems: FilterItem[] = familyMembers.map(member => ({
        id: member.id,
        type: 'label' as const,
        name: member.name,
        color: member.color,
        isSelected: selectedLabelIds.includes(member.id),
        subtitle: member.isMe ? 'You' : undefined,
      }));

      // Sort: "Me" first, then alphabetically
      labelItems.sort((a, b) => {
        if (a.subtitle === 'You') return -1;
        if (b.subtitle === 'You') return 1;
        return a.name.localeCompare(b.name);
      });

      sections.push({
        id: 'family-labels',
        title: 'Family Labels',
        items: labelItems,
      });
    }

    return sections;
  }

  /**
   * Toggles all items in a section and returns updated selections
   */
  static toggleSectionSelection(
    section: FilterSection,
    currentCalendarIds: string[],
    currentLabelIds: string[]
  ): { calendarIds: string[]; labelIds: string[] } {
    const allSelected = section.items.every(item => item.isSelected);

    let newCalendarIds = [...currentCalendarIds];
    let newLabelIds = [...currentLabelIds];

    section.items.forEach(item => {
      if (item.type === 'calendar') {
        if (allSelected) {
          // Deselect all calendars in this section
          newCalendarIds = newCalendarIds.filter(id => id !== item.id);
        } else {
          // Select all calendars in this section
          if (!newCalendarIds.includes(item.id)) {
            newCalendarIds.push(item.id);
          }
        }
      } else {
        if (allSelected) {
          // Deselect all labels in this section
          newLabelIds = newLabelIds.filter(id => id !== item.id);
        } else {
          // Select all labels in this section
          if (!newLabelIds.includes(item.id)) {
            newLabelIds.push(item.id);
          }
        }
      }
    });

    return { calendarIds: newCalendarIds, labelIds: newLabelIds };
  }

  /**
   * Toggles a single item and returns updated selections
   */
  static toggleItemSelection(
    item: FilterItem,
    currentCalendarIds: string[],
    currentLabelIds: string[],
    allCalendars: DeviceCalendar[]
  ): { calendarIds: string[]; labelIds: string[] } {
    // Prevent toggling hidden calendars (Android limitation)
    if (item.isHiddenInNativeApp) {
      return { calendarIds: currentCalendarIds, labelIds: currentLabelIds };
    }

    let newCalendarIds = [...currentCalendarIds];
    let newLabelIds = [...currentLabelIds];

    if (item.type === 'calendar') {
      if (item.isSelected) {
        // Deselecting - ensure at least one calendar remains
        const remaining = newCalendarIds.filter(id => id !== item.id);
        if (remaining.length === 0) {
          const primary = allCalendars.find(c => c.isPrimary);
          newCalendarIds = primary ? [primary.id] : [allCalendars[0]?.id].filter(Boolean);
        } else {
          newCalendarIds = remaining;
        }
      } else {
        newCalendarIds.push(item.id);
      }
    } else {
      if (item.isSelected) {
        newLabelIds = newLabelIds.filter(id => id !== item.id);
      } else {
        newLabelIds.push(item.id);
      }
    }

    return { calendarIds: newCalendarIds, labelIds: newLabelIds };
  }
}
